<template>
  <div ref="root" class="checkout-delivery">
    <ui-notification
      :title="`Не выбран способ получения для ${productsCount}`"
      class="delivery-notification"
      color="#FFB71B"
    >
      <span>
        {{ subtitle }}
      </span>

      <template slot="action">
        <div class="delivery-buttons">
          <ui-button
            v-for="button in buttons"
            :key="button.value"
            :class="['delivery-button', { hidden: button.hidden }]"
            :text="button.title"
            @click="cardSelect(button.value)"
          />
        </div>
      </template>
    </ui-notification>
  </div>
</template>

<script>
import { mapActions, mapGetters } from 'vuex';
import { pluralize } from '@/utils/string-utils';
import { cartGtmEvents } from '@/gtm/cart-gtm-events';

export default {
  name: 'checkout-delivery',

  data: () => ({
    pickupStores: {},
    pickupThirdparty: {},
  }),

  computed: {
    ...mapGetters({
      PRODUCTS: 'cart/products',
      SHIPPING_CHANNELS: 'cart/shippingChannels',
      SHIPPING_GROUPS: 'cart/shippingGroups',
    }),

    buttons() {
      const pickupGroups = this.SHIPPING_GROUPS.filter(g => ['pickup', 'thirdparty'].includes(g.type));
      const pickupProductsCount = pickupGroups.length ? this.getLineIdsCount(pickupGroups.reduce((acc, { products }) => [...acc, ...products], [])) : 0;
      const pickupSubtitle = pickupGroups.length
        ? `
          Выбран${this.$options.filters.decl(pickupGroups.length, '', ['', 'о', 'о'])}
          ${pickupGroups.length}
          ${this.$options.filters.decl(pickupGroups.length, 'пункт', ['', 'a', 'ов'])}
          самовывоза для
          ${pickupProductsCount} ${this.$options.filters.decl(pickupProductsCount, 'товар', ['а', 'ов', 'ов'])}
          `
        : 'Выбирайте удобный пункт самовывоза';

      const deliveryGroup = this.SHIPPING_GROUPS.find(g => g.type === 'delivery');
      const deliveryChannel = this.SHIPPING_CHANNELS.find(c => c.type === 'delivery');
      const deliveryProductsCount = this.getLineIdsCount(deliveryGroup?.products);
      const deliverySubtitle = deliveryProductsCount
        ? `Выбрана доставка курьером для ${deliveryProductsCount}\xa0${this.$options.filters.decl(deliveryProductsCount, 'товар', ['а', 'ов', 'ов'])}`
        : (deliveryChannel?.freeShipping && deliveryChannel.available
          ? `Бесплатно при покупке от ${this.$options.filters.price(deliveryChannel.freeShipping)} ₽`
          : 'Недоступно'
        )
      ;

      return [
        {
          title: 'Забрать самовывозом',
          subtitle: pickupSubtitle,
          value: 'pickup',
          active: !!pickupProductsCount,
          hidden: !this.SHIPPING_CHANNELS.some(c => c.type === 'pickup' && c.available),
        },
        {
          title: 'Доставить курьером',
          subtitle: deliverySubtitle,
          value: 'delivery',
          active: this.SHIPPING_GROUPS.some(g => g.type === 'delivery'),
          hidden: !this.SHIPPING_CHANNELS.some(c => c.type === 'delivery' && c.available),
        },
      ];
    },

    isOnlyOneShippingGroup() {
      return this.buttons.some(b => b.hidden);
    },

    subtitle() {
      if (this.isOnlyOneShippingGroup) {
        let items = this.getLineIdsCount(this.PRODUCTS) > 1 ? 'Товары доступны' : 'Товар доступен';
        let method = this.SHIPPING_GROUPS.some(g => g.type === 'delivery') ? 'доставки курьером' : 'самовывоза';

        return `${items} только для ${method}`;
      }
      return 'Выберите удобный способ получения';
    },

    productsCount() {
      return pluralize(this.getLineIdsCount(this.PRODUCTS), ['товара', 'товаров', 'товаров']);
    }
  },

  beforeDestroy() {
    this.$f.$off('select-pickup');
  },

  methods: {
    ...mapActions({
      SET_SHIPPING_GROUP: 'cart/setShippingGroup',
    }),

    getLineIdsCount(products = []) {
      return products.reduce((a, { lineIds = [] } = {}) => a + lineIds.length, 0);
    },

    cardSelect(type) {
      const { y: rootY } = this.$refs.root.getBoundingClientRect();
      if (type === 'pickup') {
        this.$f.$emit('allocate-pickup', { products: this.PRODUCTS, type: 'pickup' });
        this.$f.$once('select-pickup', () => this.adjustScroll(rootY));
        cartGtmEvents.shippingByPickup();
      }
      if (type === 'delivery') {
        this.$f.$emit('allocate-pickup', { products: this.PRODUCTS, type: 'courier' });
        this.$f.$once('select-pickup', () => this.adjustScroll(rootY));
        cartGtmEvents.shippingByCourier();
      }
    },

    async adjustScroll(oldY) { // корректировка скролла после изменения способа доставки
      await this.$nextTick();
      const { y: newY } = this.$refs.root.getBoundingClientRect();
      window.scrollBy(0, newY - oldY);
    },
  }
}
</script>

<style lang="scss">
.checkout-delivery {
  .delivery-notification {
    margin: 0;
    padding: 12px;

    .ui-notification-action {
      width: 100%;
    }
  }

  .delivery-buttons {
    width: 100%;
    flex-direction: column;
  }

  .delivery-button {
    width: 100%;

    &:first-child {
      margin: 0 0 8px;
    }

    &.hidden {
      display: none;
    }
  }
}

@include breakpoint("tablet") {
  .checkout-delivery {
    .delivery-notification {
      display: flex;
      flex-direction: column;
      align-items: center;

      .ui-notification-action {
        margin-top: 12px;
      }
    }

    .delivery-buttons {
      width: 100%;
      display: flex;
      flex-direction: row;
      max-height: 40px;
    }

    .delivery-button {
      &:first-child {
        margin: 0;
      }

      &:last-child {
        margin-left: 8px;
      }
    }
  }
}

@include breakpoint("desktop") {
  .checkout-delivery {
    .delivery-notification {
      flex-direction: row;
      padding: 16px 20px 16px 16px;

      .ui-notification-content {
        width: 100%;
      }

      .ui-notification-action {
        flex-shrink: 0;
        width: auto;
        margin: 0 0 0 24px;
      }

      .delivery-button {
        width: auto;

        &:last-child {
          margin-left: 12px;
        }
      }
    }
  }
}
</style>
